import { cn } from '@myosh/odin-components';
import { memo, useMemo } from 'react';
import LoadingPreview from '../../../components/common/loading-preview';
import { makeDataCy } from '../../../helpers/util';

interface DiagramLoadingProps {
  loading?: boolean;
}
interface NodesContainerProps {
  title: string;
  message: string;
  isControl?: boolean;
  classNames?: string;
}

interface HazardRiskNoteProps {
  message: string;
}

const NodesContainer = memo(function NodesContainer({
  title,
  message,
  isControl = false,
  classNames,
}: NodesContainerProps) {
  const dataCyTitle = useMemo(() => makeDataCy(title), [title]);

  return (
    <div
      data-cy={`nodes-container-${dataCyTitle}`}
      className={cn(
        'flex h-full w-full flex-col justify-center rounded-md border-2 border-dashed px-2 py-4 text-center',
        { 'w-[75%]': !isControl },
        classNames
      )}
    >
      <div>
        <h4 data-cy={`nodes-container-title-${dataCyTitle}`} className="mb-16 text-base font-semibold">
          {title}
        </h4>
      </div>
      <div className="flex h-full items-center justify-center">
        <LoadingPreview message={message} />
      </div>
    </div>
  );
});

const HazardRiskNode = memo(function HazardRiskNode({ message }: HazardRiskNoteProps) {
  const dataCyMessage = useMemo(() => makeDataCy(message), [message]);
  return (
    <div
      data-cy={`hazard-risk-node-${dataCyMessage}`}
      className="border-mue-border flex h-20 w-40 items-center justify-center rounded-md border border-dashed p-2 text-center text-xs"
    >
      <LoadingPreview message={message} />
    </div>
  );
});

const DiagramLoading = ({ loading = false }: DiagramLoadingProps) => (
  <div
    data-cy="diagram-loading"
    className={cn('bg-background absolute z-[1] flex h-full w-full space-x-4 px-16 py-12', {
      hidden: !loading,
    })}
  >
    <NodesContainer
      title="Causes"
      message="Causes will be displayed here"
      classNames="bg-causes-bg border-causes-border"
    />
    <NodesContainer
      isControl={true}
      title="Preventative Controls"
      message="Preventative Controls will be displayed here"
      classNames="bg-pc-bg border-pc-border"
    />
    <div className="mx-12 flex h-[55%] w-full flex-col items-center justify-between">
      <HazardRiskNode message="Hazard will be displayed here" />
      <HazardRiskNode message="Risk will be displayed here" />
    </div>
    <NodesContainer
      isControl={true}
      title="Mitigating Controls"
      message="Mitigating Controls will be displayed here"
      classNames="bg-mc-bg border-mc-border"
    />
    <NodesContainer
      title="Consequences"
      message="Consequences will be displayed here"
      classNames="bg-consequences-bg border-consequences-border"
    />
  </div>
);

export default memo(DiagramLoading);
